import React, {FC, useState} from 'react';
import {BSButton, BSCheckBox, BSInput} from 'bodyspec-shared-components';
import DynamicAsset from 'components/DynamicAsset';
import {DEFAULT_ERROR_MESSAGE} from 'constants/index';
import {FormikHelpers, useFormik} from 'formik';
import {omit} from 'lodash';

import {Progress} from './_Progress';
import {Socials} from './_Socials';
import {Success} from './_Success';
import {Contact} from './types';
import {getWaitListSchema} from './validations';
import {useWaitListData} from '.';

import * as styles from './styles.module.scss';

interface FormValues extends Contact {
	isSubscribed: boolean;
}

interface IContactFormProps {
	path: string;
}

enum AddState {
	INITIAL,
	PENDING,
	SUCCESS,
	ERR_EXISTS,
	ERR_UNKNOWN,
}

export const ContactForm: FC<IContactFormProps> = ({path}) => {
	const [state, setState] = useState<AddState>(AddState.INITIAL);
	const {data, onSubmit} = useWaitListData(path);

	const {errors, values, handleChange, handleSubmit, setFieldValue} = useFormik({
		initialValues: {
			firstName: '',
			lastName: '',
			email: '',
			zipCode: '',
			isSubscribed: true,
		},

		onSubmit: async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
			try {
				const contact = omit(values, ['isSubscribed']);

				setState(AddState.PENDING);
				const {data, errors} = await onSubmit(contact);
				if (errors) {
					setState(AddState.ERR_UNKNOWN);
				} else if (!data?.contact.capture) {
					setState(AddState.ERR_EXISTS);
				} else {
					setState(AddState.SUCCESS);
					formikHelpers.resetForm();
				}
			} catch (error) {
				setState(AddState.ERR_UNKNOWN);
			}
		},

		validationSchema: getWaitListSchema(
			(name) => `Please provide your ${name}`,
		),
	});

	const handleChangeField = (e: React.ChangeEvent) => {
		handleChange(e);
		setState(AddState.INITIAL);
	};

	if (state === AddState.SUCCESS) {
		return <Success location={data?.list?.location} />;
	}

	return (
		<>
			{data?.list?.logo?.url && (
				<div className={styles.imageWrapper}>
					<DynamicAsset imageSrc={data?.list?.logo?.url} />
				</div>
			)}
			<Progress data={data} />
			<form className={styles.form}>
				<div className={styles.inputContainer}>
					<BSInput
						name='firstName'
						label='First name'
						placeholder='Enter legal first name'
						value={values.firstName}
						onChange={handleChangeField}
						error={errors.firstName}
					/>
				</div>
				<div className={styles.inputContainer}>
					<BSInput
						name='lastName'
						label='Last name'
						placeholder='Enter legal last name'
						value={values.lastName}
						onChange={handleChangeField}
						error={errors.lastName}
					/>
				</div>
				<div className={styles.inputContainer}>
					<BSInput
						name='email'
						label='Email'
						placeholder='Email'
						value={values.email}
						onChange={handleChangeField}
						error={errors.email}
					/>
				</div>
				<div className={styles.inputContainer}>
					<BSInput
						name='zipCode'
						label='Zip code'
						placeholder='Enter zip code'
						value={values.zipCode}
						onChange={handleChangeField}
						error={errors.zipCode}
					/>
				</div>
			</form>
			<div className={styles.checkboxWrapper} onClick={() => {
				setFieldValue('isSubscribed', !values.isSubscribed);
			}}>
				<BSCheckBox
					className={styles.checkbox}
					checked={values.isSubscribed}
					readOnly
				/>
				<span>By joining the waitlist you agree to let BodySpec contact you once your
					event date is confirmed, so you can sign up for an appointment.</span>
			</div>
			{state === AddState.ERR_EXISTS && (
				<div className='alert alert-danger' role='alert'>
					The email you provided is already on the list.
				</div>
			)}
			{state === AddState.ERR_UNKNOWN && (
				<div className='alert alert-danger' role='alert'>
					{DEFAULT_ERROR_MESSAGE}
				</div>
			)}
			<BSButton
				className={styles.signUpBtn}
				variant='dark'
				size='lg'
				disabled={!values.isSubscribed || state === AddState.PENDING}
				onClick={() => handleSubmit()}
			>
				Sign up
			</BSButton>
			<Socials />
		</>
	);
};
